import { useState } from "react";
import { IBreadcrumb } from "../../pages/MainGridPage/MainGridPage";
import styles from "./styles/TerraceSubPageHeader.module.scss";
import ClickableLink from "../utilities/ClickableLink";
import FontAwesome from "../utilities/FontAwesome";
import { EditableTitle } from "./Breadcrumbs";
import FollowPageButton from "./FollowPageButton";
import { IMicrositeLink } from "../header/microsite/Microsite";
import classNames from "classnames";
import { observer } from "mobx-react";
import dynamic from "next/dynamic";
import { useStores } from "../../stores/useStores";
import { useTranslateClassName } from "../../hooks/useTranslateClassName";
import HorizontalScrollContainer from "./HorizontalScrollContainer";

const DraggableSubPageNavigation = dynamic(
  () => import("./TerraceSubPageNavigation"),
);

const TerraceSubPageHeader: React.FC<{
  breadcrumbs: IBreadcrumb[];
  editable?: boolean;
}> = observer((props) => {
  const { organizationStore, modalStore, userStore } = useStores();
  const [reorderingEnabled, setReorderingEnabled] = useState(false);
  const translateClassName = useTranslateClassName();
  let { breadcrumbs } = props;

  if (breadcrumbs.length > 1) {
    breadcrumbs = [
      breadcrumbs[breadcrumbs.length - 2],
      breadcrumbs[breadcrumbs.length - 1],
    ];
  } else {
    breadcrumbs = [breadcrumbs[0]];
  }

  const children: IMicrositeLink[] =
    organizationStore.currentOrganization.sub_navigations;

  const containerClassName = classNames({
    [styles.container]: true,
    [styles.containerTwoRows]: children.length > 0,
  });

  const titleClassName = classNames({
    [styles.title]: true,
    [styles.titleHomePage]: breadcrumbs.length === 1,
  });

  return (
    <div className={classNames(containerClassName, translateClassName)}>
      <div className={styles.titleAndSubSections}>
        <div>
          {breadcrumbs.map((breadcrumb) => {
            const itemKey = `${organizationStore.currentOrganization.id}-${breadcrumb?.model?.id || breadcrumb.url}`;
            return (
              <span
                key={itemKey}
                className={classNames(
                  titleClassName,
                  styles.breadcrumbSelected,
                )}
              >
                {organizationStore.currentOrganization.id ===
                breadcrumb.model?.id ? (
                  <EditableTitle
                    breadcrumb={breadcrumb}
                    editable={props.editable}
                  />
                ) : (
                  <ClickableLink
                    href={breadcrumb.url}
                    className={styles.backIcon}
                    title={breadcrumb.label}
                  >
                    {organizationStore.organization.id !==
                      organizationStore.currentOrganization.id && (
                      <FontAwesome
                        name={"fa-circle-arrow-left"}
                        prefix={"fas"}
                      />
                    )}
                  </ClickableLink>
                )}
              </span>
            );
          })}
          {children.length === 0 && userStore.editor && (
            <div className={styles.addSubPageInitial}>
              <button
                key={children.length + 1}
                className="scroll-item"
                id={`microsite-${children.length + 1}`}
                title={"Add New Sub-section"}
                onClick={() => {
                  modalStore.addModal({
                    id: "addOrEditBlock",
                    type: "addOrEditBlock",
                    intent: "add",
                    blockType: "section",
                  });
                }}
              >
                <FontAwesome prefix={"fa-solid"} name={"fa-plus"} />{" "}
                <span>Add Subpage</span>
              </button>
            </div>
          )}
        </div>
        {children.length > 0 && (
          <HorizontalScrollContainer
            scrollContainerClassName={styles.micrositeContainer}
            scrollItemClassName={"scroll-item"}
          >
            {children.map((child, idx) => {
              if (userStore.editor) {
                return (
                  <div
                    key={idx}
                    className="scroll-item"
                    style={{ display: "inline-block" }}
                  >
                    <DraggableSubPageNavigation
                      reorder={reorderingEnabled}
                      link={child}
                      index={idx}
                    />
                  </div>
                );
              } else {
                return (
                  <ClickableLink
                    key={idx}
                    className={`scroll-item ${styles.microsite}`}
                    href={child.url}
                  >
                    {child.label}
                  </ClickableLink>
                );
              }
            })}
            {userStore.editor && (
              <>
                {children.length > 1 && (
                  <button
                    key={children.length}
                    className="scroll-item"
                    title={"Reorder Sub-sections"}
                    onClick={() => setReorderingEnabled((v) => !v)}
                  >
                    <FontAwesome
                      prefix={"fa-solid"}
                      name={"fa-arrows-left-right"}
                      style={{
                        borderRadius: 5,
                        border: `3px solid ${reorderingEnabled ? "#545454" : "transparent"}`,
                      }}
                    />
                  </button>
                )}
                <button
                  key={children.length + 1}
                  className="scroll-item"
                  title={"Add New Sub-section"}
                  onClick={() => {
                    modalStore.addModal({
                      id: "addOrEditBlock",
                      type: "addOrEditBlock",
                      intent: "add",
                      blockType: "section",
                    });
                  }}
                >
                  <FontAwesome prefix={"fa-solid"} name={"fa-plus"} />
                </button>
              </>
            )}
          </HorizontalScrollContainer>
        )}
      </div>
      <FollowPageButton
        initialIsFollower={userStore.follower}
        symbolOnly={true}
        className={styles.followButton}
        size={"2x"}
        organizationTitle={organizationStore.currentOrganization.title}
        toggleFollowingOrganizationId={organizationStore.currentOrganization.id}
      />
    </div>
  );
});

export default TerraceSubPageHeader;
